import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { LoginUserProfile, UserResponse } from '../../types/UserTypes';
import { formatDate } from '../utils/dateTimeUtils';
import DataList from './DataList';
import ProfileIcon from './icons/ProfileIcon';

interface Props {
  data?: LoginUserProfile;
  editUserData?: UserResponse;
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: theme.xPalette.lightGrey,
    fontWeight: theme.typography.h1.fontWeight,
    backgroundColor: theme.xPalette.background,
    borderBottom: 0,
    fontSize: 16,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 16,
    fontWeight: theme.typography.h4.fontWeight,
  },
}));

export default function UserInfoCard({ data, editUserData }: Props) {
  const { t } = useTranslation();
  const { clinicAccountName, clinicSites, accountId, userRole } = data || {};
  const { clinicAccountName: editUserAccountName, clinicAccount, dateCreated } = editUserData || {};
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const clinicSiteLabels = {
    clinicSiteName: t('user.editProfile.form.clinicSiteName'),
    clinicSiteId: t('user.editProfile.form.clinicSiteId'),
    clinicSiteExternalId: t('user.editProfile.form.clinicSiteExternalId'),
  };

  const renderMobileData = (data: NonNullable<LoginUserProfile['clinicSites']>) => {
    return (
      <DataList
        lineHeight={'auto'}
        data={data.map((site) => {
          const listItems = [
            [
              <>{clinicSiteLabels.clinicSiteName}</>,
              <Box sx={{ wordBreak: 'break-word' }}>{site.name}</Box>,
            ],
            [<>{clinicSiteLabels.clinicSiteId}</>, <>{site.id}</>],
            [
              <>{clinicSiteLabels.clinicSiteExternalId}</>,
              <Box sx={{ wordBreak: 'break-word' }}>{site.externalId || '-'}</Box>,
            ],
          ];
          return {
            leftHeader: (
              <Typography variant="body1" fontWeight="bold" fontSize={18}>
                Label
              </Typography>
            ),
            rightHeader: (
              <Typography variant="body1" fontWeight="bold" fontSize={18}>
                Value
              </Typography>
            ),
            items: [...listItems],
          };
        })}
        customStyle={{
          borderRadius: '16px 16px 0 0',
        }}
      />
    );
  };

  const renderDesktopData = (data: NonNullable<LoginUserProfile['clinicSites']>) => {
    return (
      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: '12px 12px 0 0' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <StyledTableCell>{clinicSiteLabels.clinicSiteName}</StyledTableCell>
              <StyledTableCell>{clinicSiteLabels.clinicSiteId}</StyledTableCell>
              <StyledTableCell>{clinicSiteLabels.clinicSiteExternalId}</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((site) => (
              <TableRow key={site.id}>
                <StyledTableCell>{site.name}</StyledTableCell>
                <StyledTableCell>{site.id}</StyledTableCell>
                <StyledTableCell>{site.externalId || '-'}</StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex' }}>
        <ProfileIcon />
        <Box sx={(theme) => ({ color: theme.xPalette.lightGrey, flex: 1 })}>
          {(clinicAccountName || editUserAccountName) && (
            <Typography variant="body1">
              {t('user.editProfile.form.accountName')}
              <Typography component="span" sx={{ fontWeight: 600, color: theme.xPalette.black }}>
                {(clinicAccountName && accountId && `${clinicAccountName} (${accountId})`) ||
                  (editUserAccountName &&
                    clinicAccount &&
                    `${editUserAccountName} (${clinicAccount})`) ||
                  ''}
              </Typography>
            </Typography>
          )}
          {userRole && (
            <Typography variant="body1">
              {t('user.editProfile.form.userRole')}
              <Typography component="span" sx={{ fontWeight: 600, color: theme.xPalette.black }}>
                {userRole}
              </Typography>
            </Typography>
          )}
          {clinicSites && (
            <>
              <Typography variant="body1">{t('user.editProfile.form.siteName')}</Typography>
              {isDesktop ? renderDesktopData(clinicSites) : renderMobileData(clinicSites)}
            </>
          )}
          {dateCreated && (
            <Typography variant="body1">
              {t('user.manageUsers.userForm.creationDate')}
              <Typography component="span" sx={{ fontWeight: 600, color: theme.xPalette.black }}>
                {formatDate(dateCreated)}
              </Typography>
            </Typography>
          )}
        </Box>
      </Box>
    </Box>
  );
}
